import { environment } from '@internal/environment/environment';
import { SystemPageKey } from '@internal/page/SystemPageKey';
import { SearchPageType } from '@internal/page/TSystemPageData';
import { logger } from '@internal/utils/logger/logger';
import { getServerSideMachineTypeForMachineTypeSlug } from '@internal/utils/machine/getServerSideMachineTypeForMachineTypeSlug';
import { TLocale } from '@internal/utils/machine/i18n/TLocale';
import {
    hotDealsRoute,
    searchRoute,
    TSearchResultsPageRouteParams,
} from '@internal/utils/routing/routingTable';
import { DefaultQueryParams } from '@internal/utils/routing/urls/DefaultQueryParams';
import {
    CookieSchema,
    setUserCountryCodeCookie,
} from '@internal/utils/user/cookie';
import { fallbackUserCountryCode } from '@internal/utils/user/User';
import Cookies from 'cookies';
import type { GetServerSideProps, NextPage } from 'next';
import { BasePage } from 'page/BasePage';
import { TPageProps } from 'page/TPageProps';
import React from 'react';
import { getRedirect } from 'services/cmsClient';
import { RequestCombiner } from 'services/RequestCombiner';
import { defaultLocale, undefinedLocale } from 'utils/constants';
import { initFeatureFlags } from 'utils/featureFlags/initFeatureFlags';
import { getPageProps } from 'utils/page/getPageProps';
import { PageTypeKey } from 'utils/page/PageTypeKey';
import { getRequestIp, isValidIp } from 'utils/request/getRequestIp';
import * as router from 'utils/routing/router';
import { urlFor } from 'utils/routing/urls/urlFor';

const setUserCountryCodeCookieHoc =
    (
        fn: GetServerSideProps<TPageProps, DefaultQueryParams>,
    ): GetServerSideProps<TPageProps, DefaultQueryParams> =>
    async context => {
        const result = await fn(context);
        if (result && 'props' in result) {
            const props = await Promise.resolve(result.props);
            const cookie = CookieSchema.parse(context.req.cookies);
            if (
                props.meta.user?.countryCode &&
                // only set the cookie if the cookie values has changed
                cookie.userCountryCode !== props.meta.user.countryCode
            ) {
                setUserCountryCodeCookie({
                    countryCode: props.meta.user.countryCode,
                    response: context.res,
                });
            }
        }
        return result;
    };

export const getServerSideProps: GetServerSideProps<
    TPageProps,
    DefaultQueryParams
> = setUserCountryCodeCookieHoc(async context => {
    const { slugs, ff, ...restQueryParams } =
        context.query as DefaultQueryParams;
    const queryParams = restQueryParams;
    const { userCountryCode: userCountryCookie } = CookieSchema.parse(
        context.req.cookies,
    );
    // allow override of country via query param for testing
    const countryOverride = queryParams.country?.toUpperCase() ?? null;
    // allow override of ip via query param for testing
    const ip = isValidIp(queryParams.ip)
        ? queryParams.ip
        : getRequestIp(context.req);
    const userCountryCode =
        countryOverride ??
        userCountryCookie ??
        // country cannot be derived when running locally
        (environment.NODE_ENV === 'development'
            ? fallbackUserCountryCode
            : null);

    const host = context.req.headers.host ?? null;
    const locale =
        context.locale === undefinedLocale
            ? (defaultLocale as TLocale)
            : (context.locale as TLocale);
    const requestCombiner = new RequestCombiner();

    try {
        // Next.js 15 does not include trailing slash in resolvedUrl for data fetching requests
        // Normalize the URL to always have a trailing slash for consistency with trailingSlash: true
        let url = context.resolvedUrl.split('?')[0];
        if (!url.endsWith('/')) {
            url = `${url}/`;
        }

        if (!router.isValidRoutingUrl(url)) {
            return {
                notFound: true,
            };
        }
        const slug = Array.from(slugs ?? []).join('/');

        const featureFlags = initFeatureFlags(
            new Cookies(context.req, context.res),
            ff,
        );

        // 1 SYSTEM PAGE ____________________________________________________________
        const systemPageRoutingResult = router.matchSystemPage(
            locale,
            urlFor(locale, url),
            queryParams,
        );

        if (systemPageRoutingResult) {
            switch (systemPageRoutingResult.systemPageKey) {
                case SystemPageKey.PDP: {
                    const machineType =
                        getServerSideMachineTypeForMachineTypeSlug(
                            locale,
                            systemPageRoutingResult.pathParams.machineType,
                        );

                    if (!machineType) {
                        return {
                            notFound: true,
                        };
                    }
                    return getPageProps({
                        type: PageTypeKey.SystemPage,
                        key: SystemPageKey.PDP,
                        context,
                        queryParams,
                        pathParams: systemPageRoutingResult.pathParams,
                        ip,
                        host,
                        requestCombiner,
                        featureFlags,
                        machineType,
                        userCountryCode,
                    });
                }

                case SystemPageKey.SearchResults: {
                    const pathParams: TSearchResultsPageRouteParams['pathParams'] =
                        systemPageRoutingResult.pathParams;

                    if (
                        pathParams.machineTypeSlug === hotDealsRoute[locale] ||
                        pathParams.machineTypeSlug === searchRoute[locale]
                    ) {
                        return getPageProps({
                            type: PageTypeKey.SystemPage,
                            key: SystemPageKey.SearchResults,
                            context,
                            machineType: null,
                            queryParams,
                            pathParams,
                            ip,
                            host,
                            requestCombiner,
                            featureFlags,
                            userCountryCode,
                            searchPageType:
                                pathParams.machineTypeSlug ===
                                hotDealsRoute[locale]
                                    ? SearchPageType.HotDeals
                                    : SearchPageType.GeneralSearch,
                        });
                    } else if (pathParams.machineTypeSlug) {
                        const machineType =
                            getServerSideMachineTypeForMachineTypeSlug(
                                locale,
                                pathParams.machineTypeSlug,
                            );

                        if (!machineType) {
                            return {
                                notFound: true,
                            };
                        }

                        return getPageProps({
                            type: PageTypeKey.SystemPage,
                            key: SystemPageKey.SearchResults,
                            context,
                            machineType,
                            queryParams,
                            pathParams,
                            ip,
                            host,
                            requestCombiner,
                            featureFlags,
                            userCountryCode,
                            searchPageType: SearchPageType.GeneralSearch,
                            machineCategoryPageData: {
                                machineType,
                                machineTypeSlug: pathParams.machineTypeSlug,
                                brandSlug: pathParams.brandSlug,
                                modelSlug: pathParams.modelSlug,
                            },
                        });
                    }
                    break;
                }
                default: {
                    // Fix to satisfy TS type checker:
                    // Only allow SystemPageKey keys which are allowed by getPageProps' TParams
                    switch (systemPageRoutingResult.systemPageKey) {
                        case SystemPageKey.CompareMachines:
                        case SystemPageKey.Dashboard:
                        case SystemPageKey.InternalServerError:
                        case SystemPageKey.NotFound:
                        case SystemPageKey.StartPage: {
                            return getPageProps({
                                type: PageTypeKey.SystemPage,
                                key: systemPageRoutingResult.systemPageKey,
                                context,
                                queryParams,
                                pathParams: systemPageRoutingResult.pathParams,
                                ip,
                                host,
                                requestCombiner,
                                featureFlags,
                                userCountryCode,
                            });
                        }
                        default:
                            // TypeScript: explicitly handle remaining SystemPageKeys not accepted else case
                            // This should never happen unless TParams/config types mismatch.
                            return {
                                notFound: true,
                            };
                    }
                }
            }
        }

        // 4 SHORT PDP Redirect _____________________________________________________
        const shortPDPRedirectRoutingResult = await router.matchRedirect({
            type: 'short-pdp',
            url: urlFor(locale, url),
            queryParams,
            locale,
            ip,
        });

        if (shortPDPRedirectRoutingResult) {
            return {
                redirect: {
                    statusCode: 301,
                    destination: shortPDPRedirectRoutingResult.target,
                },
            };
        }

        // 5 CMS PAGE _______________________________________________________________
        const pageProps = await getPageProps({
            type: PageTypeKey.Page,
            slug: encodeURIComponent(slug),
            context,
            queryParams,
            pathParams: {},
            ip,
            host,
            requestCombiner,
            featureFlags,
            userCountryCode,
        });

        if (!('notFound' in pageProps) || !pageProps.notFound) {
            return pageProps;
        }

        // 6 SERP REDIRECTS _________________________________________________________
        const serpRedirectRoutingResult = await router.matchRedirect({
            type: 'serp',
            url: urlFor(locale, url),
            queryParams,
            locale,
            ip,
        });

        if (serpRedirectRoutingResult) {
            return {
                redirect: {
                    statusCode: 301,
                    destination: serpRedirectRoutingResult.target,
                },
            };
        }

        // 7 CMS REDIRECTS __________________________________________________________
        const redirect = await getRedirect(
            encodeURIComponent(`/${locale}/${slug}/`),
        );
        if (redirect) {
            return {
                redirect: {
                    statusCode: 301,
                    destination: redirect,
                },
            };
        }

        // 8 ALL OTHER INTERNAL REDIRECTS ___________________________________________
        const redirectRoutingResult = await router.matchRedirect({
            type: 'other',
            url: urlFor(locale, url),
            queryParams,
            locale,
            ip,
        });

        if (redirectRoutingResult) {
            return {
                redirect: {
                    statusCode: 301,
                    destination: redirectRoutingResult.target,
                },
            };
        }

        // 9 NOT FOUND ______________________________________________________________
        return {
            notFound: true,
        };
    } catch (err) {
        logger.error(err);

        context.res.statusCode = 500;

        return getPageProps({
            type: PageTypeKey.SystemPage,
            key: SystemPageKey.InternalServerError,
            context,
            queryParams,
            pathParams: {},
            ip,
            host,
            requestCombiner,
            featureFlags: {},
            userCountryCode,
        });
    }
});

const Page: NextPage<TPageProps> = props => <BasePage {...props} />;

export default React.memo(Page);
